/* eslint-disable @typescript-eslint/no-explicit-any */
import { CONST } from '@common/constants/const.constant';
import { FormAdditionalContext, IFormAdditionalContextProps } from '@common/general-ui/form/form.component';
import { IDictionary } from '@common/interfaces/dictionary.interface';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { PathValue, RegisterOptions, useFormContext, UseFormWatch, ValidationRule } from 'react-hook-form';

const LATIN_REGEXP = /[/`/\t/\n/\r a-zA-Z0-9/[/$/€/&/+/,/:/;/”/“/=/?/@/#/|/'/</>/./^/*/(/)/%/!/\\//{/}///-/"/'_\]\-~£]/g;
const NOT_LATIN_REGEXP_AND_FULL_LINE =
    /^[/`/\t/\n/\r a-zA-Z0-9/[/$/€/&/+/,/:/;/”/“/=/?/@/#/|/'/</>/./^/*/(/)/%/!/\\//{/}///-/"/®/™/©/'_\]\-~£]*$/g;

interface IUseFieldReturn<T> {
    errorMessage: string | undefined;
    setFieldValue: (newValue: T) => void;
    setFieldTouched: () => void;
    triggerValidate: () => void;
    value: T;
}

interface IProps extends RegisterOptions {
    name: any;
    getCustomErrorMessage: ((opts: RegisterOptions) => RegisterOptions) | undefined;
}

// Why use this code instead register method -> forwardRef problem with antd - works ok with native inputs but with antd have troubles - manually fire event.
// also discussion about problem with ref https://stackoverflow.com/questions/58703615/react-hook-form-with-antd-styling
export const useField = <T>({ name, getCustomErrorMessage, ...rest }: IProps): IUseFieldReturn<T> => {
    const formContext = useFormContext<T>();
    const {
        formState: { errors: fieldsErrors, touchedFields },
        register, // onChange, onBlur, name, ref!!!
        unregister,
        watch,
        trigger,
        setValue,
    } = formContext;

    const formAdditionalContext = useContext<IFormAdditionalContextProps<any>>(FormAdditionalContext);
    const registerProps = register(name, {
        ...rest,
        ...(getCustomErrorMessage
            ? getCustomErrorMessage(getDefaultValidationMessages(rest, name, watch))
            : getDefaultValidationMessages(rest, name, watch)),
    });

    const { onFieldBlur, onFieldChange, registerBlurHandler } = formAdditionalContext;
    const { onBlur } = registerProps;
    useEffect(() => {
        registerBlurHandler(name, onBlur);
    }, [registerBlurHandler, name, onBlur]);

    const fieldValue = watch(name);
    const valueRef = useRef<any>(fieldValue);
    valueRef.current = fieldValue;

    useEffect(() => {
        return (): void => unregister(name);
    }, [name, unregister]);
    const triggerValidate = useCallback(() => {
        trigger(name);
    }, [trigger, name]);

    const setFieldValue = useCallback(
        (newValue: T) => {
            if (valueRef.current !== newValue) {
                valueRef.current = newValue;
                setValue(name, newValue as any);
                onFieldChange(name as string);
            }
        },
        [name, onFieldChange, setValue],
    );

    const setFieldTouched = useCallback(() => {
        // don't forget pass name to onBlur event. If missed - touched don't work correctly
        // if call manually - don't forget add type='blur'
        onBlur({
            type: 'blur',
            target: {
                name,
                value: valueRef.current,
            },
        });
        if (valueRef.current && typeof valueRef.current === 'string') {
            setValue(name, (valueRef.current as any).trim());
        }
        onFieldBlur(name as string);
    }, [name, onBlur, onFieldBlur, setValue]);

    const fieldError = (fieldsErrors as IDictionary<any>)[name];
    const fieldIsTouched = touchedFields ? (touchedFields as IDictionary<any>)[name] : false;
    const fieldErrorMessage = fieldIsTouched && fieldError?.type ? fieldError.message : undefined;
    return useMemo(() => {
        return {
            value: fieldValue as T,
            triggerValidate,
            setFieldValue,
            setFieldTouched,

            errorMessage: fieldErrorMessage,
        };
    }, [fieldValue, triggerValidate, setFieldValue, setFieldTouched, fieldErrorMessage]);
};

function getDefaultValidationMessages(
    { required, minLength, maxLength, min, max }: RegisterOptions,
    name: PathValue<any, any>,
    watch: UseFormWatch<any>,
): RegisterOptions {
    const result: RegisterOptions = {} as RegisterOptions;
    if (required) {
        result.required = CONST.thisFieldRequired;
    }
    if (maxLength !== undefined && maxLength > 0) {
        result.maxLength = {
            value: maxLength,
            message: `Reduce the amount of characters to a maximum of ${maxLength} characters)`,
        } as ValidationRule<number>;
    }
    if (max !== undefined && max > 0) {
        result.max = {
            value: max,
            message: `This number is too large (max ${max})`,
        } as ValidationRule<number>;
    }
    if (min !== undefined) {
        result.min = {
            value: min,
            message: `This number is too low (min ${min})`,
        } as ValidationRule<number>;
    }
    if (minLength !== undefined && minLength > 0) {
        result.minLength = {
            value: minLength,
            message: `You require a minimum of ${minLength} characters)`,
        } as ValidationRule<number>;
    }
    const value = watch(name);
    result.pattern = {
        value: NOT_LATIN_REGEXP_AND_FULL_LINE,
        message: 'Only latin symbols allowed. Please remove these characters and try again: ' + getNotLatinSymbols(value),
    };
    return result;
}

function getNotLatinSymbols(value: unknown): string {
    if (typeof value === 'string') {
        return (value as string)
            .replaceAll(LATIN_REGEXP, '')
            .split('')
            .map((symbol) => `'${symbol}'`)
            .join();
    }
    return '';
}
